/**
 * Where a survivor wakes up after being downed.
 *
 * Janeks: *"after being down, really spawn at the medical shack at home."*
 *
 * ─── What was actually wrong ────────────────────────────────────────────────
 * The server has always done its half properly: `HealthService.dealDamage` sets
 * `atMedCenter = true` and revives the player at a fraction of max HP
 * (health-recovery.md rule 9 — "respawned at the settlement Med-Center"). The
 * CLIENT then ignored it completely. `createBaseSim` took `world.spawnPoint`
 * and nothing else, so a player who had just bled out in a duel stood up at the
 * settlement's ordinary front-gate spawn, a full map away from the Medbay, with
 * 10% HP and no explanation of where they were. The Med-Center existed as a
 * database flag and a building, and the two had never been connected.
 *
 * ─── Deriving the spot, rather than authoring it ────────────────────────────
 * The obvious move is a hand-placed coordinate. It would go stale on the next
 * bake: `home_settlement.ts` is regenerated WHOLESALE by the editor, and the
 * ids in it are editor-generated and unstable across bakes (the checklist
 * records both). So the building is found by `modelUrl` — which is content, not
 * a generated id — and the standing spot is computed from the building's OWN
 * footprint and rotation, the same discipline `author_cables.ts` uses when it
 * derives cable anchors from each object's own `size.h` instead of a stored
 * literal. Move the Medbay in the editor and this follows it.
 *
 * ─── Which side of the building ─────────────────────────────────────────────
 * Toward the middle of the settlement, NOT along the building's own facing.
 * Nothing in `WorldObject` records which way a model's door is cut, so reading
 * `rotationY` as "the front" would be a guess that is wrong for half the
 * catalog and silently drops the player behind the building. The town side is
 * derivable, is where the door faces in practice, and degrades gracefully: the
 * worst case is standing beside the Medbay rather than inside a wall.
 */
import type { Vec2, WorldData, WorldObject } from '../types';

/** The Medbay's model. Content, unlike the editor-generated object ids. */
const MED_CENTER_MODEL = '/models/buildings/building_settlement_medbay.glb';

/** The NPC who runs it — the fallback identity if the model is ever re-exported under a new name. */
const MED_CENTER_NPC = 'iris';

/** Metres of clear ground between the building's footprint and the player's feet. */
const DOORSTEP_CLEARANCE = 1.6;

export function findMedCenter(world: WorldData): WorldObject | undefined {
  return world.objects.find((o) => o.modelUrl === MED_CENTER_MODEL)
    ?? world.objects.find((o) => o.npcId === MED_CENTER_NPC && o.kind === 'building');
}

/**
 * How far an oriented box reaches from its centre along `dir` (a unit vector on
 * the ground plane).
 *
 * The support function of a rotated rectangle:
 * `|halfW * (dir . localX)| + |halfD * (dir . localZ)|`. Using the plain
 * half-width instead would put the player inside the corner of any building
 * that is not axis-aligned — and the Medbay is rotated -1.48 rad, which is very
 * much not.
 *
 * Pure and exported so the geometry can be asserted without a scene.
 */
export function boxExtentAlong(
  halfW: number,
  halfD: number,
  rotationY: number,
  dir: Vec2,
): number {
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);
  // Local axes of the box in world space. `rotationY` rotates about +Y.
  const axisX = { x: cos, z: -sin };
  const axisZ = { x: sin, z: cos };
  const dotX = dir.x * axisX.x + dir.z * axisX.z;
  const dotZ = dir.x * axisZ.x + dir.z * axisZ.z;
  return Math.abs(halfW * dotX) + Math.abs(halfD * dotZ);
}

/**
 * Where to stand when waking at the Med-Center, or `null` if this map has no
 * Medbay (every map but the settlement).
 *
 * `null` rather than a fallback coordinate: the caller then keeps the map's own
 * `spawnPoint`, which is the right answer for a map with no Med-Center in it.
 * Inventing a position here would put the player somewhere arbitrary on maps
 * this rule was never meant to touch.
 */
export function medCenterSpawn(world: WorldData): Vec2 | null {
  const med = findMedCenter(world);
  if (!med) return null;

  const { x, z } = med.position;
  // Toward the settlement's middle. A building sitting exactly on the origin
  // has no "toward the middle", so fall back to -Z, the direction the player
  // already faces on spawn (`base-sim.ts` sets `facing: Math.PI`).
  const len = Math.hypot(x, z);
  const dir: Vec2 = len > 1e-6 ? { x: -x / len, z: -z / len } : { x: 0, z: -1 };

  // `collisionW`/`collisionD` when present — they are the footprint the sim
  // actually pushes the player out of, and they differ from `size` on this very
  // building (6.48 x 7.29 against a 10.8 x 12.15 visual). Standing clear of the
  // visual but inside the collider would spawn the player stuck.
  const halfW = (med.collisionW ?? med.size.w) / 2;
  const halfD = (med.collisionD ?? med.size.d) / 2;
  const reach = boxExtentAlong(halfW, halfD, med.rotationY ?? 0, dir) + DOORSTEP_CLEARANCE;

  return { x: x + dir.x * reach, z: z + dir.z * reach };
}
